/**
 * The console — the frame the player is actually looking at.
 *
 * The fiction is that this is a control surface inside the factory ship, not a
 * web page about a factory ship. That is a functional decision more than a
 * decorative one: a console shows ONE screen at a time behind a rail of
 * physical-feeling buttons, which is the only way eleven panels' worth of
 * readouts fit somewhere legible. The previous layout stacked all eleven in a
 * single scrolling column — 3,360 pixels of content in a 1,000 pixel window, so
 * the player saw thirty per cent of the game at any moment and had to scroll
 * past the build queue to find the drones.
 *
 * Screens group readings that are consulted together. A player asking "why is
 * nothing being built" wants the queue, the material stocks and the mech count
 * in one glance, not scattered down a column between the market and the events
 * log.
 *
 * Boundary: this file is chrome. It never reads simulation state except to hand
 * a snapshot to the panels it owns, and it never issues a command of its own.
 */

import { el } from './panels/panel';
import type { Panel } from './panels/panel';
import type { GameState } from '../sim/types';

export interface ScreenSpec {
  /** Stable id, used for the remembered screen and for the layout key. */
  id: string;
  /** Rail button text. Kept short — the rail is a column of hardware keys. */
  label: string;
  /** A glyph above the label. Decorative; the label is what is read. */
  glyph: string;
  /** One line under the screen title saying what this screen is for. */
  caption: string;
  /** The panels on this screen, in order. */
  panels: Panel[];
  /**
   * Built into the screen's main area, left of the panels. A canvas map, or
   * nothing for screens that are all readouts.
   */
  feature?: HTMLElement;
}

export interface ConsoleShell {
  root: HTMLElement;
  /** Re-render the visible screen. Hidden screens are not updated. */
  update(state: GameState): void;
  /** The id of the screen currently lit. */
  activeId(): string;
  show(id: string): void;
  /** Move `delta` screens along the rail, wrapping at both ends. */
  rotate(delta: number): void;
}

const ACTIVE_KEY = 'heliocenter.activeScreen';

function readActive(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}

function writeActive(id: string): void {
  try {
    window.localStorage.setItem(ACTIVE_KEY, id);
  } catch {
    // Storage disabled or full. The screen simply will not be remembered.
  }
}

/**
 * True when a keystroke belongs to something the player is typing into.
 *
 * Without this, setting a synthesis target to 200 would rotate the console
 * twice on the way past the arrow keys, and the number box would lose focus
 * mid-entry.
 */
function typingInto(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function createConsole(
  screens: ScreenSpec[],
  onScreenChange: (id: string) => void,
): ConsoleShell {
  if (screens.length === 0) throw new Error('the console needs at least one screen');

  const root = el('div', 'console');
  const rail = el('nav', 'rail');
  const stage = el('div', 'stage');

  const buttons = new Map<string, HTMLButtonElement>();
  const views = new Map<string, HTMLElement>();

  screens.forEach((screen, index) => {
    // Each screen owns one of the theme's six hues, so the rail reads as six
    // distinct keys rather than six shades of the same one. It also carries a
    // CONTRASTING hue, taken from the far side of the ring, because the edges
    // are lit from two directions — see the two-tone edge in style.css.
    const hue = `var(--hue-${(index % 6) + 1})`;
    const hueAlt = `var(--hue-${((index + 3) % 6) + 1})`;

    const button = el('button', 'rail-key');
    button.style.setProperty('--hue', hue);
    button.style.setProperty('--hue-alt', hueAlt);
    button.append(
      el('span', 'rail-glyph', screen.glyph),
      el('span', 'rail-label', screen.label),
      // The lamp is the only part that changes, so it gets its own node rather
      // than a class on the button that would restyle the whole key.
      el('span', 'rail-lamp'),
    );
    button.title = screen.caption;
    button.addEventListener('click', () => {
      show(screen.id);
    });
    rail.append(button);
    buttons.set(screen.id, button);

    const view = el('div', 'screen');
    view.dataset['screen'] = screen.id;
    view.style.setProperty('--hue', hue);
    view.style.setProperty('--hue-alt', hueAlt);

    const header = el('div', 'screen-head');
    header.append(
      el('span', 'screen-title', screen.label),
      el('span', 'screen-caption', screen.caption),
    );

    const bodyRow = el('div', 'screen-body');

    if (screen.feature !== undefined) {
      const featureWrap = el('div', 'screen-feature');
      featureWrap.append(screen.feature);
      bodyRow.append(featureWrap);
      bodyRow.classList.add('has-feature');
    }

    // Panels live in their own scrolling column so a long readout never pushes
    // the map off the screen.
    const column = el('div', 'screen-panels');
    for (const panel of screen.panels) column.append(panel.root);
    bodyRow.append(column);

    view.append(header, bodyRow);
    views.set(screen.id, view);
    stage.append(view);
  });

  // A first-time visitor gets the first screen on the rail, which is the
  // introduction. Anyone who has been here before resumes where they left off:
  // being returned to the intro every session would be a nuisance rather than a
  // welcome.
  const remembered = readActive();
  let activeId =
    remembered !== null && views.has(remembered) ? remembered : (screens[0] as ScreenSpec).id;

  function show(id: string): void {
    if (!views.has(id)) return;
    activeId = id;
    for (const [screenId, view] of views) {
      const on = screenId === id;
      view.classList.toggle('screen-on', on);
      buttons.get(screenId)?.classList.toggle('rail-key-on', on);
    }
    writeActive(id);
    // Canvases on a hidden screen measure zero, so whoever owns them has to
    // re-measure and redraw now that this one has size.
    onScreenChange(id);
  }

  function rotate(delta: number): void {
    const index = screens.findIndex((screen) => screen.id === activeId);
    if (index < 0) return;
    // Modulo twice so a negative delta wraps to the end rather than to NaN.
    const next = (((index + delta) % screens.length) + screens.length) % screens.length;
    show((screens[next] as ScreenSpec).id);
  }

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.defaultPrevented || typingInto(event.target)) return;
    if (event.ctrlKey || event.altKey || event.metaKey) return;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') rotate(1);
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') rotate(-1);
    else return;

    // Only after a key we actually handled, so arrows still scroll elsewhere.
    event.preventDefault();
  };

  window.addEventListener('keydown', onKeyDown);

  // A window listener outlives a hot reload, so without this every save during
  // development leaves another copy attached and one arrow press eventually
  // rotates the console several screens at once. Dev-only; in a built bundle
  // the module is evaluated once and import.meta.hot is undefined.
  import.meta.hot?.dispose(() => {
    window.removeEventListener('keydown', onKeyDown);
  });

  root.append(rail, stage);
  show(activeId);

  return {
    root,
    activeId: () => activeId,
    show,
    rotate,
    update(state: GameState): void {
      // Only the visible screen. With twenty-odd drones the field readout is
      // the expensive one, and there is no reason to lay it out while the
      // player is looking at the market.
      const screen = screens.find((candidate) => candidate.id === activeId);
      if (screen === undefined) return;
      for (const panel of screen.panels) panel.update(state);
    },
  };
}
